/**
 * Batch Filters Panel Component
 * Comprehensive filtering options for available batches
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-005
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import type { BatchFilters } from '@/types/availableBatches';
import { EXPIRY_RANGE_OPTIONS } from '@/types/availableBatches';
import { ProductType, QualityGrade, ProductTypeConfig, QualityGradeConfig } from '@/types/inventory';
import {
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Package,
  Award,
  Shield,
  Calendar,
  MapPin,
  AlertTriangle
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface BatchFiltersPanelProps {
  filters: BatchFilters;
  onFiltersChange: (filters: BatchFilters) => void;
  onReset: () => void;
  activeFilterCount: number;
  className?: string;
}

export function BatchFiltersPanel({
  filters,
  onFiltersChange,
  onReset,
  activeFilterCount,
  className
}: BatchFiltersPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleProductTypeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      productType: value as ProductType | 'ALL'
    });
  };

  const handleQualityGradeToggle = (grade: QualityGrade) => {
    const newGrades = filters.qualityGrades.includes(grade)
      ? filters.qualityGrades.filter(g => g !== grade)
      : [...filters.qualityGrades, grade];

    onFiltersChange({
      ...filters,
      qualityGrades: newGrades
    });
  };

  const handlePesoToggle = (value: boolean) => {
    onFiltersChange({
      ...filters,
      pesoCompliant: value ? true : null
    });
  };

  const handleExpiryRangeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      expiryRange: value as BatchFilters['expiryRange']
    });
  };

  const handleStorageLocationChange = (value: string) => {
    onFiltersChange({
      ...filters,
      storageLocation: value
    });
  };

  const handleLowStockToggle = (checked: boolean) => {
    onFiltersChange({
      ...filters,
      lowStockOnly: checked
    });
  };

  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <CardTitle className="text-lg">Filters</CardTitle>
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {activeFilterCount} active
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onReset}
                className="text-gray-600 hover:text-gray-900"
              >
                <X className="mr-1 h-4 w-4" />
                Clear All
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-6">
          {/* Product Type Filter */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Package className="h-4 w-4" />
              Product Type
            </Label>
            <Select value={filters.productType} onValueChange={handleProductTypeChange}>
              <SelectTrigger>
                <SelectValue placeholder="All product types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Product Types</SelectItem>
                {Object.entries(ProductTypeConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    <span className="flex items-center gap-2">
                      <span>{config.icon}</span>
                      <span>{config.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Quality Grade Filter (Multiple Selection) */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Award className="h-4 w-4" />
              Quality Grade
            </Label>
            <div className="space-y-2">
              {Object.entries(QualityGradeConfig).map(([key, config]) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={`grade-${key}`}
                    checked={filters.qualityGrades.includes(key as QualityGrade)}
                    onCheckedChange={() => handleQualityGradeToggle(key as QualityGrade)}
                  />
                  <label
                    htmlFor={`grade-${key}`}
                    className="flex-1 text-sm cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Grade {key}</span>
                      <span className="text-xs text-gray-500">{config.description}</span>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* PESO Compliance Filter */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Shield className="h-4 w-4" />
              PESO Compliance
            </Label>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-700">
                {filters.pesoCompliant === null
                  ? 'Show all batches'
                  : 'Show PESO compliant only'}
              </span>
              <Switch
                checked={filters.pesoCompliant === true}
                onCheckedChange={handlePesoToggle}
              />
            </div>
          </div>

          {/* Expiry Range Filter */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Calendar className="h-4 w-4" />
              Expiry Range
            </Label>
            <Select value={filters.expiryRange} onValueChange={handleExpiryRangeChange}>
              <SelectTrigger>
                <SelectValue placeholder="All batches" />
              </SelectTrigger>
              <SelectContent>
                {EXPIRY_RANGE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Storage Location Filter */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <MapPin className="h-4 w-4" />
              Storage Location
            </Label>
            <Input
              type="text"
              placeholder="Filter by location..."
              value={filters.storageLocation}
              onChange={(e) => handleStorageLocationChange(e.target.value)}
            />
          </div>

          {/* Low Stock Only Toggle */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <AlertTriangle className="h-4 w-4" />
              Stock Alert
            </Label>
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
              <span className="text-sm text-amber-900 font-medium">
                Show low stock batches only
              </span>
              <Switch
                checked={filters.lowStockOnly}
                onCheckedChange={handleLowStockToggle}
              />
            </div>
          </div>

          {/* Active Filters Summary */}
          {activeFilterCount > 0 && (
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 font-medium">
                  {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} applied
                </span>
                <Button
                  variant="link"
                  size="sm"
                  onClick={onReset}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Reset All
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
